import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Loader } from '@lucid/lego';
import { useStore } from '../../../store/store';
import Table from './Table/Table';
import { useGetFSE } from '../../../api/services/lupp/fse/fse';
import { convertToContentfulLocale } from '../../../utils/tools';
import { useGetLabels } from '../../../hooks/useLabels';
import styles from './FseTechSupport.module.scss';

const FseTechSupport: React.FC = () => {
  const locale = useStore((state) => state.locale);
  const theme = useStore((state) => state.theme);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  const {
    data: fseData,
    isLoading,
    isFetching,
    error,
  } = useGetFSE({
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch || undefined,
    enabled: !labelsLoading && !labelsError,
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    // Scroll to top when page changes
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const renderContent = () => {
    if (labelsLoading || isLoading) {
      return (
        <Box className={styles.loaderContainer}>
          <Loader size={48} />
        </Box>
      );
    }

    if (labelsError) {
      return (
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_leads}
          </Typography>
        </Box>
      );
    }

    return (
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} />
          </Box>
        )}
        <Table
          theme={theme}
          data={fseData?.items || []}
          totalCount={fseData?.totalCount || 0}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        FSE Tech Support
      </Typography>
      {renderContent()}
    </Box>
  );
};

export default FseTechSupport;
